import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Download,
  Search,
  SlidersHorizontal,
  ArrowRight,
  ClipboardCheck,
} from "lucide-react";
import { useLeads } from "../hooks/useLeads";
import LeadCard from "../components/leads/LeadCard";
import LeadFilters from "../components/leads/LeadFilters";
import LeadExport from "../components/leads/LeadExport";
import LoadingSpinner from "../components/common/LoadingSpinner";
import LeadPipeline from "../components/leads/LeadPipeline";
import { LEAD_STATUS } from "../config/constants";

const PAGE_SIZES = [6, 12, 24];
const STATUS_KEYS = Object.values(LEAD_STATUS);

const LeadsPage = () => {
  const { leads = [], loading, error: leadsError, refetch } = useLeads() || {};

  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    source: "all",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search || "");
      setPage(1);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [filters.search]);

  const filteredLeads = useMemo(() => {
    if (!leads || leads.length === 0) return [];

    let result = [...leads];

    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      result = result.filter((lead) => {
        const fullName = `${lead.firstName || ""} ${
          lead.lastName || ""
        }`.toLowerCase();
        return (
          fullName.includes(s) ||
          (lead.email || "").toLowerCase().includes(s) ||
          (lead.phone || "").includes(debouncedSearch) ||
          (lead.company || "").toLowerCase().includes(s)
        );
      });
    }

    if (filters.status && filters.status !== "all") {
      result = result.filter((l) => l.status === filters.status);
    }

    if (filters.source && filters.source !== "all") {
      result = result.filter((l) => l.source === filters.source);
    }

    return result;
  }, [leads, debouncedSearch, filters.status, filters.source]);

  const statusBreakdown = useMemo(() => {
    return STATUS_KEYS.reduce((acc, key) => {
      acc[key] = filteredLeads.filter((lead) => lead.status === key).length;
      return acc;
    }, {});
  }, [filteredLeads]);

  const total = filteredLeads.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pageLeads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLeads.slice(start, start + pageSize);
  }, [filteredLeads, page, pageSize]);

  const handleRefresh = useCallback(async () => {
    setLocalError(null);
    try {
      if (typeof refetch === "function") {
        await refetch();
      } else {
        window.location.reload();
      }
    } catch (err) {
      setLocalError(
        "Impossible de rafraichir les prospects. Verifiez votre connexion."
      );
      console.error("Refresh leads failed:", err);
    }
  }, [refetch]);

  const exportToCSV = useCallback(async (items, filename = "leads.csv") => {
    setExporting(true);
    try {
      if (!items || items.length === 0) {
        setLocalError("Aucun prospect a exporter.");
        return;
      }
      const headers = [
        "id",
        "firstName",
        "lastName",
        "email",
        "phone",
        "company",
        "status",
        "source",
        "createdAt",
      ];
      const csvRows = [
        headers.join(","),
        ...items.map((it) =>
          headers
            .map((h) => {
              const val = it[h] ?? "";
              if (val && val.toDate && typeof val.toDate === "function") {
                return `"${val.toDate().toISOString()}"`;
              }
              return `"${String(val).replace(/"/g, '""')}"`;
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      setLocalError("Echec de l'export. Reessayez.");
    } finally {
      setExporting(false);
    }
  }, []);

  const handleFilterChange = useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ search: "", status: "all", source: "all" });
    setPage(1);
  }, []);

  const recommendations = useMemo(() => {
    const suggestions = [];

    const newCount = statusBreakdown[LEAD_STATUS.NEW] || 0;
    const negotiationCount = statusBreakdown[LEAD_STATUS.NEGOTIATION] || 0;
    const lostCount = statusBreakdown[LEAD_STATUS.LOST] || 0;

    if (newCount > 0) {
      suggestions.push({
        title: "Prioriser les nouveaux prospects",
        message: `${newCount} prospects n'ont pas encore ete contactes. Planifiez un premier echange dans les 24h.`,
        link: `/leads?status=${LEAD_STATUS.NEW}`,
      });
    }

    if (negotiationCount > 0) {
      suggestions.push({
        title: "Conclure les negociations",
        message: `Vous avez ${negotiationCount} negociations actives. Preparez une offre finale pour maximiser vos chances.`,
        link: `/leads?status=${LEAD_STATUS.NEGOTIATION}`,
      });
    }

    if (lostCount > 0) {
      suggestions.push({
        title: "Analyser les dossiers perdus",
        message: `Identifiez les freins des ${lostCount} dossiers perdus pour ajuster votre proposition de valeur.`,
        link: `/leads?status=${LEAD_STATUS.LOST}`,
      });
    }

    if (suggestions.length < 3) {
      suggestions.push(
        {
          title: "Segmenter votre portefeuille",
          message:
            "Appliquez un filtre par source pour adapter vos scripts et personnaliser votre approche commerciale.",
          link: "/leads",
        },
        {
          title: "Planifier des relances",
          message:
            "Bloquez des creneaux dedies aux relances hebdomadaires pour garder un pipeline actif et previsible.",
          link: "/leads",
        }
      );
    }

    return suggestions.slice(0, 3);
  }, [statusBreakdown]);

  const quickFilters = useMemo(
    () => [
      { label: "Tous", value: "all" },
      { label: "A traiter", value: LEAD_STATUS.NEW },
      { label: "En contact", value: LEAD_STATUS.CONTACTED },
      { label: "Qualifies", value: LEAD_STATUS.QUALIFIED },
      { label: "Negociation", value: LEAD_STATUS.NEGOTIATION },
      { label: "Gagnes", value: LEAD_STATUS.CONVERTED },
    ],
    []
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-page)]">
        <LoadingSpinner size="lg" aria-label="Chargement des prospects" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-page)]">
      <main className="container-custom py-8 space-y-8">
        <section className="panel-surface p-6 space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <p className="text-sm uppercase tracking-wide text-[var(--color-text-muted)]">
                Gestion des prospects
              </p>
              <h1 className="text-3xl font-semibold text-[var(--color-text-primary)]">
                Mes prospects
              </h1>
              <p className="text-sm text-[var(--color-text-secondary)]">
                {total} prospect(s) correspondant(s) a vos filtres actuels.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                <input
                  type="search"
                  value={filters.search}
                  onChange={(e) =>
                    handleFilterChange({ search: e.target.value })
                  }
                  placeholder="Rechercher nom, email, societe..."
                  className="input-field pl-10"
                  aria-label="Rechercher des prospects"
                />
              </div>
              <Link to="/leads/add" className="btn btn-primary">
                <Plus className="w-4 h-4" />
                Nouveau prospect
              </Link>
              <button
                onClick={() => setShowExport((s) => !s)}
                className="btn btn-outline"
                aria-pressed={showExport}
              >
                <Download className="w-4 h-4" /> Export
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-[var(--color-text-muted)] uppercase">
              Filtres rapides
            </span>
            {quickFilters.map((option) => {
              const isActive =
                filters.status === option.value ||
                (option.value === "all" && filters.status === "all");
              return (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange({ status: option.value })}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                    isActive
                      ? "border-[var(--color-brand)] text-[var(--color-brand)] bg-[var(--color-brand-soft)]"
                      : "border-[var(--color-border-soft)] text-[var(--color-text-muted)] hover:border-[var(--color-border-strong)]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-brand)] hover:text-[var(--color-brand-strong)]"
            >
              <SlidersHorizontal className="w-4 h-4" /> Reinitialiser
            </button>
          </div>
        </section>

        <LeadPipeline counts={statusBreakdown} total={filteredLeads.length} />

        <div className="grid gap-6 lg:grid-cols-[2.5fr,1fr]">
          <section className="panel-surface p-6 space-y-6">
            <div className="panel-quiet p-4">
              <LeadFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClear={clearFilters}
              />
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-[var(--color-text-muted)]">
                  Affichage
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="input-field w-32"
                  aria-label="Taille de page"
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s} / page
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    exportToCSV(filteredLeads, `leads_export_${Date.now()}.csv`)
                  }
                  disabled={exporting}
                  className="btn btn-outline"
                  aria-label="Exporter les prospects en CSV"
                >
                  {exporting ? (
                    <>
                      <LoadingSpinner size="sm" /> Export...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" /> Exporter CSV
                    </>
                  )}
                </button>
                <button
                  onClick={handleRefresh}
                  className="btn btn-secondary"
                  aria-label="Rafraichir la liste"
                >
                  Rafraichir
                </button>
              </div>
            </div>

            {(leadsError || localError) && (
              <div className="panel-quiet p-4 border border-danger/30 text-danger text-sm">
                {leadsError?.message || localError}
              </div>
            )}

            {pageLeads.length === 0 ? (
              <div className="panel-quiet p-10 text-center">
                <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                  Aucun prospect ne correspond a vos criteres.
                </p>
                <Link to="/leads/add" className="btn btn-primary">
                  <Plus className="w-4 h-4" /> Ajouter un prospect
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {pageLeads.map((lead) => (
                  <LeadCard key={lead.id || lead.uid || lead._id} lead={lead} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-[var(--color-text-muted)]">
                  Page {page} sur {totalPages} ? {total} resultat(s)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn btn-outline disabled:opacity-50"
                  >
                    Precedent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(
                      Math.max(0, page - 3),
                      Math.min(totalPages, page + 2)
                    )
                    .map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        aria-current={p === page ? "page" : undefined}
                        className={`px-3 py-2 rounded-lg border transition-colors ${
                          p === page
                            ? "border-[var(--color-brand)] text-[var(--color-brand)]"
                            : "border-[var(--color-border-soft)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)]"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="btn btn-outline disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </section>

          <aside className="space-y-6">
            {showExport && (
              <section className="panel-surface p-6 space-y-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                  <Download className="w-4 h-4" /> Export avance
                </h3>
                <LeadExport
                  leads={filteredLeads}
                  onExport={(format) => {
                    if (format === "csv") {
                      exportToCSV(
                        filteredLeads,
                        `leads_export_${Date.now()}.csv`
                      );
                    }
                    setShowExport(false);
                  }}
                />
                <button
                  onClick={() => setShowExport(false)}
                  className="btn btn-outline w-full"
                >
                  Fermer
                </button>
              </section>
            )}

            <section className="panel-surface p-6 space-y-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                <ClipboardCheck className="w-4 h-4" /> Recommandations
              </h3>
              <ul className="space-y-3">
                {recommendations.map((item) => (
                  <li key={item.title} className="panel-quiet p-4">
                    <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {item.title}
                    </h4>
                    <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
                      {item.message}
                    </p>
                    <Link
                      to={item.link}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-brand)] hover:text-[var(--color-brand-strong)]"
                    >
                      Explorer <ArrowRight className="w-3 h-3" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default LeadsPage;
